import React, { useState, useRef } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import ReCAPTCHA from "react-google-recaptcha";
import { https, environment } from "../../http";
import { setCookie, decrypt } from "../../utils";
import { RECAPTCHA_SITE_KEY_ENC } from "../../constants";
import { getProfile } from "../../services/profile";
import "./QRScan.css";


let RECAPTCHA_SITE_KEY;
try {
  RECAPTCHA_SITE_KEY = decrypt(RECAPTCHA_SITE_KEY_ENC);
  if (!RECAPTCHA_SITE_KEY || RECAPTCHA_SITE_KEY.length < 10) throw new Error("bad decrypt");
} catch {
  RECAPTCHA_SITE_KEY = "6LdtbZcsAAAAANzSiAuZqwotIRsyGa9hhGURcT00";
}

const getInitials = (name = "") =>
  name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();

const QRScan = () => {
  const { clientId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const clientName = searchParams.get("name") || "Client";
  const displayRef = searchParams.get("ref") || "";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaError, setCaptchaError] = useState("");
  const [loading, setLoading] = useState(false);
  const [locationStatus, setLocationStatus] = useState(null);
  const recaptchaRef = useRef(null);

  const handleGoogleSuccess = async (credentialResponse) => {
    setError("");
    setLoading(true);
    try {
      const response = await https().post("/auth/google-auth", {
        client_id: credentialResponse?.clientId,
        jwtToken: credentialResponse?.credential,
      });
      if (response?.status === 200) {
        const { token } = response.data;
        setCookie(token, "token");
        localStorage.setItem(
          "qr_session",
          JSON.stringify({ clientId, name: clientName, ref: displayRef, loggedInAt: new Date().toISOString() })
        );
        await getProfile(false, token);
        navigate("/mobile-chart", { state: { clientId } });
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Google sign-in failed.");
    } finally {
      setLoading(false);
    }
  };

  const finishLogin = async (response) => {
    const { message, token } = response?.data ?? {};
    if (message === "Email Verified" && token) {
      setCookie(token, "token");
      localStorage.setItem(
        "qr_session",
        JSON.stringify({ clientId, name: clientName, ref: displayRef, loggedInAt: new Date().toISOString() })
      );
      await getProfile(false, token);
      navigate("/mobile-chart", { state: { clientId } });
    } else if (!token) {
      setError(
        "Two-factor authentication is enabled on this account. Please use the regular login page."
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setCaptchaError("");
    setLocationStatus(null);
    if (!captchaToken && environment !== "local") {
      setCaptchaError("Please complete the CAPTCHA");
      return;
    }
    setLoading(true);
    try {
      let response;
      try {
        response = await https().post("/auth/login", { email, password, captchaToken });
      } catch (err) {
        const data = err?.response?.data;
        if (err?.response?.status === 403 && data?.loginLocationRequired) {
          if (data?.loginLocationDenied) {
            setLocationStatus({ status: "denied", message: data.message });
            return;
          }
          setLocationStatus({ status: "acquiring", message: "Verifying your location..." });
          try {
            const pos = await new Promise((resolve, reject) =>
              navigator.geolocation.getCurrentPosition(resolve, reject, {
                enableHighAccuracy: true,
                timeout: 15000,
              })
            );
            const loginLocation = { lat: pos.coords.latitude, lng: pos.coords.longitude };
            setLocationStatus({ status: "verifying", message: "Checking distance to facility..." });
            response = await https().post("/auth/login", { email, password, captchaToken, loginLocation });
            setLocationStatus({ status: "success", message: "Location verified!" });
          } catch (locErr) {
            if (locErr?.response?.data?.loginLocationDenied) {
              setLocationStatus({ status: "denied", message: locErr.response.data.message });
            } else {
              setLocationStatus({
                status: "error",
                message: "Unable to get your location. Please enable location services and try again.",
              });
            }
            return;
          }
        } else {
          throw err;
        }
      }
      await finishLogin(response);
    } catch (err) {
      const msg = err?.response?.data?.message || "Invalid email or password.";
      setError(msg);
    } finally {
      setLoading(false);
      recaptchaRef.current?.reset();
      setCaptchaToken(null);
    }
  };

  return (
    <div className="qrs-root">
      <div className="qrs-card">

        {/* Centered header: logo + title + subtitle */}
        <div className="qrs-login-header">
          <div className="qrs-logo-mark">O</div>
          <h1 className="qrs-title">Sign in to Ogiso</h1>
          <p className="qrs-subtitle">Continue to chart for this client</p>
        </div>

        {/* Client preview card */}
        <div className="qrs-preview" aria-label="Client scanned">
          <div className="qrs-avatar-wrap">
            <div className="qrs-avatar-inner">
              {getInitials(clientName)}
            </div>
          </div>
          <div className="qrs-preview-info">
            <div className="qrs-scanned-label">You scanned</div>
            <div className="qrs-client-name">{clientName}</div>
            <div className="qrs-client-ref">{displayRef}</div>
          </div>
        </div>

        {/* Form */}
        <form className="qrs-form" onSubmit={handleSubmit} noValidate>
          <div className="qrs-field">
            <label htmlFor="qrs-email">Email</label>
            <input
              id="qrs-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoComplete="email"
              autoFocus
            />
          </div>

          <div className="qrs-field">
            <label htmlFor="qrs-password">Password</label>
            <div className="qrs-password-wrap">
              <input
                id="qrs-password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                className="qrs-eye"
                tabIndex={-1}
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          {error && <p className="qrs-error" role="alert">{error}</p>}

          {locationStatus && (
            <div className={`qrs-location-status qrs-location-status--${locationStatus.status}`}>
              {(locationStatus.status === "acquiring" || locationStatus.status === "verifying") && (
                <div className="qrs-location-checking">
                  <span className="qrs-spinner" />
                  <span>{locationStatus.message}</span>
                </div>
              )}
              {locationStatus.status === "denied" && (
                <div className="qrs-location-denied">
                  <strong>Login restricted by location</strong>
                  <p>{locationStatus.message}</p>
                </div>
              )}
              {locationStatus.status === "error" && (
                <div className="qrs-location-error">
                  <strong>Location required</strong>
                  <p>{locationStatus.message}</p>
                </div>
              )}
              {locationStatus.status === "success" && (
                <div className="qrs-location-success">
                  <span>{locationStatus.message}</span>
                </div>
              )}
            </div>
          )}

          {environment !== "local" && (
            <div className="qrs-captcha">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={RECAPTCHA_SITE_KEY}
                onChange={(token) => { setCaptchaToken(token); setCaptchaError(""); }}
                onExpired={() => setCaptchaToken(null)}
              />
              {captchaError && <p className="qrs-error" role="alert">{captchaError}</p>}
            </div>
          )}

          <button
            type="submit"
            className="qrs-submit"
            disabled={loading || !email || !password}
          >
            {loading && !locationStatus
              ? "Signing in…"
              : locationStatus?.status === "denied" || locationStatus?.status === "error"
              ? "Try Again"
              : "Sign in"}
          </button>

          <div className="qrs-divider"><span>OR</span></div>

          <div className="qrs-google-wrapper">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={() => setError("Google sign-in failed.")}
              size="large"
              text=""
              logo_alignment="center"
              shape="rectangular"
            />
          </div>

          <div className="qrs-forgot-row">
            <Link to="/forget-password" className="qrs-forgot">Forgot password?</Link>
          </div>
        </form>

      </div>
    </div>
  );
};

export default QRScan;
